import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const PayoffStrategyModal = ({ optimization, onClose }) => {
  const getStrategyIcon = (strategy) => {
    const iconMap = {
      'avalanche': 'TrendingDown',
      'snowball': 'Target',
      'hybrid': 'Zap'
    };
    return iconMap?.[strategy] || 'FileText';
  };

  const getPriorityColor = (priority) => {
    if (priority === 'high') return 'var(--color-destructive)';
    if (priority === 'medium') return 'var(--color-warning)';
    return 'var(--color-success)';
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-16">
      <div className="bg-card rounded-lg shadow-elevation-4 w-full max-w-4xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-24 border-b border-border sticky top-0 bg-card z-10">
          <div className="flex items-center gap-12">
            <div className="w-40 h-40 rounded-lg bg-primary/10 flex items-center justify-center">
              <Icon name="Sparkles" size={20} color="var(--color-primary)" />
            </div>
            <h2 className="text-xl font-semibold text-card-foreground">AI Debt Optimization Strategy</h2>
          </div>
          <button
            onClick={onClose}
            className="p-8 hover:bg-muted rounded-lg transition-smooth min-h-[44px] min-w-[44px] flex items-center justify-center"
            aria-label="Close modal"
          >
            <Icon name="X" size={20} color="var(--color-muted-foreground)" />
          </button>
        </div>

        <div className="p-24 space-y-24">
          <div className="bg-primary/10 rounded-lg p-20 border border-primary/20">
            <div className="flex items-center gap-12 mb-12">
              <Icon
                name={getStrategyIcon(optimization?.recommendedStrategy)}
                size={24}
                color="var(--color-primary)"
              />
              <h3 className="text-lg font-semibold text-foreground capitalize">
                Recommended Strategy: {optimization?.recommendedStrategy}
              </h3>
            </div>
            <p className="text-foreground leading-relaxed">
              {optimization?.strategyRationale}
            </p>
          </div>

          <div>
            <h3 className="text-lg font-semibold text-foreground mb-16">Debt Priority & Payment Plan</h3>
            <div className="space-y-12">
              {optimization?.debtPriority?.map((debt, index) => (
                <div
                  key={index}
                  className="bg-muted/50 rounded-lg p-16 border border-border"
                >
                  <div className="flex items-start justify-between mb-12">
                    <div className="flex items-center gap-12">
                      <div className="w-32 h-32 rounded-full bg-primary flex items-center justify-center">
                        <span className="text-sm font-bold text-white">{debt?.priority}</span>
                      </div>
                      <div>
                        <h4 className="font-semibold text-foreground">{debt?.debtName}</h4>
                        <p className="text-sm text-muted-foreground">Priority {debt?.priority}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-sm text-muted-foreground">Optimal Payment</p>
                      <p className="text-xl font-bold text-primary">
                        ${debt?.optimalPayment?.toFixed(2)}/mo
                      </p>
                    </div>
                  </div>
                  <p className="text-sm text-foreground leading-relaxed">
                    {debt?.reasoning}
                  </p>
                </div>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-16">
            <div className="bg-success/10 rounded-lg p-20 border border-success/20">
              <div className="flex items-center gap-12 mb-12">
                <Icon name="Calendar" size={20} color="var(--color-success)" />
                <h4 className="font-semibold text-foreground">Projected Timeline</h4>
              </div>
              <div className="space-y-8">
                <div className="flex justify-between">
                  <span className="text-sm text-muted-foreground">Payoff Date</span>
                  <span className="text-sm font-semibold text-foreground">
                    {optimization?.projectedPayoffTimeline?.payoffDate}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-muted-foreground">Total Months</span>
                  <span className="text-sm font-semibold text-foreground">
                    {optimization?.projectedPayoffTimeline?.totalMonths} months
                  </span>
                </div>
              </div>
            </div>

            <div className="bg-warning/10 rounded-lg p-20 border border-warning/20">
              <div className="flex items-center gap-12 mb-12">
                <Icon name="DollarSign" size={20} color="var(--color-warning)" />
                <h4 className="font-semibold text-foreground">Interest Savings</h4>
              </div>
              <div className="space-y-8">
                <div className="flex justify-between">
                  <span className="text-sm text-muted-foreground">Total Interest</span>
                  <span className="text-sm font-semibold text-foreground">
                    ${optimization?.projectedPayoffTimeline?.totalInterestPaid?.toFixed(2)}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-muted-foreground">Potential Savings</span>
                  <span className="text-sm font-semibold text-success">
                    ${optimization?.projectedPayoffTimeline?.potentialSavings?.toFixed(2)}
                  </span>
                </div>
              </div>
            </div>
          </div>

          <div>
            <h3 className="text-lg font-semibold text-foreground mb-16">Actionable Steps</h3>
            <div className="space-y-12">
              {optimization?.actionableSteps?.map((step, index) => (
                <div
                  key={index}
                  className="flex items-start gap-12 p-16 bg-muted/50 rounded-lg border border-border"
                >
                  <div
                    className="w-24 h-24 rounded-full flex items-center justify-center flex-shrink-0 mt-2"
                    style={{ backgroundColor: `${getPriorityColor(step?.priority)}20` }}
                  >
                    <Icon
                      name="CheckCircle"
                      size={14}
                      color={getPriorityColor(step?.priority)}
                    />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-8 mb-4">
                      <span className="text-sm font-semibold text-foreground">{step?.step}</span>
                      <span
                        className="text-xs px-8 py-2 rounded-full capitalize"
                        style={{
                          backgroundColor: `${getPriorityColor(step?.priority)}20`,
                          color: getPriorityColor(step?.priority)
                        }}
                      >
                        {step?.priority} priority
                      </span>
                    </div>
                    <p className="text-sm text-muted-foreground">{step?.impact}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {optimization?.budgetOptimization?.length > 0 && (
            <div>
              <h3 className="text-lg font-semibold text-foreground mb-16">Budget Optimization Tips</h3>
              <div className="bg-muted/50 rounded-lg p-20 border border-border">
                <ul className="space-y-8">
                  {optimization?.budgetOptimization?.map((tip, index) => (
                    <li key={index} className="flex items-start gap-12">
                      <Icon name="Lightbulb" size={16} color="var(--color-warning)" className="flex-shrink-0 mt-2" />
                      <span className="text-sm text-foreground">{tip}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          )}

          <div className="flex justify-end pt-16 border-t border-border">
            <Button onClick={onClose}>
              Got It
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PayoffStrategyModal;